/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  ExtendedTooltip,
  FlexContainer,
  Modal,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { sum } from "~/tools";
import { NoData } from "../../NoData";
import { SetupBudget } from "./SetupBudget";

type Props = {
  year: string;
  shouldAllowGeneration: boolean;
  previousYear: number[];
  onManualBudgetCreation: (value: number) => Promise<void>;
  getGeneratedBudget: () => void;
};

export function EmptyBudget({
  year,
  shouldAllowGeneration,
  previousYear,
  onManualBudgetCreation,
  getGeneratedBudget,
}: Props) {
  const [openAddBudget, setOpenAddBudget] = React.useState(false);
  const [budget, setBudget] = React.useState<number | undefined>();

  const prevYear = dayjs(year, dateFormat.year)
    .subtract(1, "year")
    .format(dateFormat.year);

  const disableBudgetGeneration = !shouldAllowGeneration;

  const caption = disableBudgetGeneration
    ? "Budget cannot be generated due to lack of spends in last 6 months"
    : null;

  const lastTwelveMonthsSpend = React.useMemo(
    () => sum(previousYear),
    [previousYear]
  );

  return (
    <>
      <FlexContainer
        justifyContent="center"
        direction="column"
        data-testid="empty-budget"
      >
        <NoData
          message={
            <FlexContainer direction="column">
              <Text
                as="header"
                size="lg"
                weight="medium"
                mb={theme.spacing.spacing02}
              >
                The Budget is not defined
              </Text>
              <Text size="sm" color={theme.color.text.text02}>
                Select one of the options below to define a New Budget
              </Text>
            </FlexContainer>
          }
        />
        <Spacer height={theme.spacing.spacing04} />
        <FlexContainer gap={theme.spacing.spacing06}>
          <Box boxStyle="lightGrey" css={{ maxWidth: "30rem" }}>
            <FlexContainer direction="column">
              <Text
                nowrap
                as="header"
                size="lg"
                weight="medium"
                mb={theme.spacing.spacing02}
              >
                Create New Budget Automatically
              </Text>
              <Text
                px={theme.spacing.spacing06}
                size="sm"
                tag="div"
                align="center"
                color={theme.color.text.text02}
              >
                IBM&nbsp;Multicloud&nbsp;Accelerator will create a New Budget
                for {year} based on your spend for {prevYear}. The New Budget
                can be edited at a later stage.
              </Text>
              <Spacer height={theme.spacing.spacing04} />
              <ExtendedTooltip caption={caption}>
                <Button
                  disabled={disableBudgetGeneration}
                  data-testid="budget-automatic-create"
                  onClick={() => getGeneratedBudget()}
                >
                  Create automatically
                </Button>
              </ExtendedTooltip>
            </FlexContainer>
          </Box>
          <Box boxStyle="lightGrey" css={{ maxWidth: "30rem" }}>
            <FlexContainer direction="column">
              <Text
                as="header"
                size="lg"
                weight="medium"
                mb={theme.spacing.spacing02}
              >
                Create New Budget Manually
              </Text>
              <Text
                px={theme.spacing.spacing06}
                size="sm"
                tag="div"
                align="center"
                color={theme.color.text.text02}
              >
                You can manually create New Budget for {year}.
                IBM&nbsp;Multicloud&nbsp;Accelerator will equally divide its
                value among quarters and months.
              </Text>
              <Spacer height={theme.spacing.spacing04} />
              <Button
                data-testid="budget-manual-create"
                onClick={() => setOpenAddBudget(true)}
              >
                Create manually
              </Button>
            </FlexContainer>
          </Box>
        </FlexContainer>
      </FlexContainer>
      <Modal
        isOpen={openAddBudget}
        contentLabel={`Set Budget for ${year}`}
        actions={[
          {
            order: 0,
            label: "Cancel",
            onAction: () => setOpenAddBudget(false),
            severity: "medium",
          },
          {
            order: 1,
            label: "Set Budget",
            onAction: async () => {
              if (budget != null) {
                await onManualBudgetCreation(budget);
                setBudget(undefined);
                setOpenAddBudget(false);
              }
            },
            severity: "high",
            disabled: !hasBudget(budget),
          },
        ]}
        onClose={() => setOpenAddBudget(false)}
      >
        <SetupBudget
          budget={budget}
          setBudget={setBudget}
          lastTwelveMonthsSpend={lastTwelveMonthsSpend}
        />
      </Modal>
    </>
  );
}

function hasBudget(budget: number | undefined) {
  return budget != null && budget > 0;
}
